import { Suspense } from 'react';
import { FormattedMessage } from 'react-intl';
import { Outlet, matchPath, useLocation, useMatch } from 'react-router-dom';

import { catchAll } from '@sorare/routing';

import { ErrorBoundary } from '@sorare/error-boundary';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import {
  FOOTBALL_MARKET_COMMON_PACKS,
  FOOTBALL_MARKET_COMMON_PACKS_GROUP,
  FOOTBALL_MARKET_GO_PRO,
  FOOTBALL_MARKET_INSTANT_BUY,
  FOOTBALL_MARKET_MANAGER_SALES,
  FOOTBALL_MARKET_MANAGER_SALES_PLAYERSLUG_RARITY,
  FOOTBALL_MARKET_NEW_SEASON,
  FOOTBALL_MARKET_NEW_SIGNINGS,
} from '@sorare/core/src/constants/__generated__/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useSimplifiedNav } from '@sorare/core/src/hooks/commonGame/useSimplifiedNav';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useUnclaimedMarketTasksCount } from '@sorare/core/src/hooks/users/useUnclaimedMarketTasksCount';
import { navLabels, transferMarket } from '@sorare/core/src/lib/glossary';

import { MarketBanners } from '@sorare/marketplace/src/components/MarketBanners';
import { MarketHeader } from '@sorare/marketplace/src/components/MarketHeader';
import { DisabledMarket } from '@sorare/marketplace/src/components/market/DisabledMarket';
import { EnterFromAndroidMarketPlaceTab } from '@sorare/marketplace/src/components/mobileApp/EnterFromAndroidMarketplaceTab';
import { CurrentMarketplaceUserProvider } from '@sorare/marketplace/src/contexts/currentMarketplaceUser/Provider';
import {
  buildMarketTabRoute,
  useNavigableMarketTabParams,
} from '@sorare/marketplace/src/hooks/useNavigableMarketTabParams';

const MarketLayout = () => {
  const { isIosApp, isUSIosApp, canUseIosPostMessageAndForYou, isAndroidApp } =
    useIsMobileApp();
  const {
    flags: {
      buyOnWebMarketplaceFromAndroid = false,
      useRedirectToNewSeasonPage = false,
    },
  } = useFeatureFlags();
  const { filterQuery, filterQueryWithInSeason } =
    useNavigableMarketTabParams();
  const { pathname } = useLocation();
  const { showSimplifiedNav } = useSimplifiedNav(Sport.FOOTBALL);
  const unclaimedMarketTasksCount = useUnclaimedMarketTasksCount();

  const isCommonPacksTab = !!matchPath(FOOTBALL_MARKET_COMMON_PACKS, pathname);

  const matchCommonMarketRoute = useMatch(
    catchAll(FOOTBALL_MARKET_COMMON_PACKS)
  );

  const getMarketTabRoute = (route: string, inSeason?: boolean) =>
    buildMarketTabRoute(
      route,
      inSeason ? filterQueryWithInSeason : filterQuery
    );

  const disabledMarket =
    isAndroidApp && buyOnWebMarketplaceFromAndroid && !matchCommonMarketRoute;
  const content = (
    <Suspense fallback={<LoadingIndicator fullHeight />}>
      <CurrentMarketplaceUserProvider sport={Sport.FOOTBALL}>
        {disabledMarket ? <DisabledMarket /> : <Outlet />}
      </CurrentMarketplaceUserProvider>
    </Suspense>
  );

  if (matchPath(FOOTBALL_MARKET_COMMON_PACKS_GROUP, pathname)) {
    return content;
  }

  if (isIosApp && !isUSIosApp) {
    return (
      <FullWidthContainer>
        <ContentWithStickyHeader
          header={
            <TabBar fullWidth variant="flat" ignoreQueryParams>
              {showSimplifiedNav ? (
                <>
                  <Tab
                    to={FOOTBALL_MARKET_COMMON_PACKS}
                    matches={[FOOTBALL_MARKET_COMMON_PACKS]}
                  >
                    <Badge badgeContent={unclaimedMarketTasksCount}>
                      <FormattedMessage {...navLabels.commonPacks} />
                    </Badge>
                  </Tab>
                  <Tab
                    to={FOOTBALL_MARKET_GO_PRO}
                    matches={[FOOTBALL_MARKET_GO_PRO]}
                  >
                    <FormattedMessage {...navLabels.pro} />
                  </Tab>
                </>
              ) : (
                <>
                  {!!useRedirectToNewSeasonPage && (
                    <Tab to={getMarketTabRoute(FOOTBALL_MARKET_NEW_SEASON)}>
                      <FormattedMessage
                        id="market.newSeason.sorare26"
                        defaultMessage="Sorare 26"
                      />
                    </Tab>
                  )}
                  <Tab to={getMarketTabRoute(FOOTBALL_MARKET_INSTANT_BUY)}>
                    <FormattedMessage {...transferMarket.instantBuy} />
                  </Tab>
                  <Tab to={getMarketTabRoute(FOOTBALL_MARKET_NEW_SIGNINGS)}>
                    <FormattedMessage {...navLabels.auctions} />
                  </Tab>
                  <Tab to={FOOTBALL_MARKET_COMMON_PACKS}>
                    <Badge badgeContent={unclaimedMarketTasksCount}>
                      <FormattedMessage {...navLabels.commonPacks} />
                    </Badge>
                  </Tab>
                </>
              )}
            </TabBar>
          }
          hideHeader={!canUseIosPostMessageAndForYou}
        >
          {content}
        </ContentWithStickyHeader>
      </FullWidthContainer>
    );
  }

  return (
    <FullWidthContainer>
      <ErrorBoundary fallback={null}>
        {!isCommonPacksTab && !showSimplifiedNav && <MarketBanners />}
      </ErrorBoundary>
      <ContentWithStickyHeader
        header={
          <FullWidthContainer.NoPadding>
            <FullWidthContainer>
              <EnterFromAndroidMarketPlaceTab />
              <NoIndex />
              <MarketHeader />
            </FullWidthContainer>
            <TabBar fullWidth variant="flat" ignoreQueryParams>
              {showSimplifiedNav ? (
                <>
                  <Tab
                    to={FOOTBALL_MARKET_COMMON_PACKS}
                    matches={[FOOTBALL_MARKET_COMMON_PACKS]}
                  >
                    <Badge badgeContent={unclaimedMarketTasksCount}>
                      <FormattedMessage {...navLabels.commonPacks} />
                    </Badge>
                  </Tab>
                  <Tab
                    to={FOOTBALL_MARKET_GO_PRO}
                    matches={[FOOTBALL_MARKET_GO_PRO]}
                  >
                    <FormattedMessage {...navLabels.pro} />
                  </Tab>
                </>
              ) : (
                <>
                  {!!useRedirectToNewSeasonPage && (
                    <Tab to={getMarketTabRoute(FOOTBALL_MARKET_NEW_SEASON)}>
                      <FormattedMessage {...navLabels.newSeason} />
                    </Tab>
                  )}
                  <Tab to={getMarketTabRoute(FOOTBALL_MARKET_NEW_SIGNINGS)}>
                    <FormattedMessage {...navLabels.auctions} />
                  </Tab>
                  <Tab
                    to={getMarketTabRoute(FOOTBALL_MARKET_INSTANT_BUY)}
                    matches={[FOOTBALL_MARKET_INSTANT_BUY]}
                  >
                    <FormattedMessage {...transferMarket.instantBuy} />
                  </Tab>

                  <Tab
                    to={getMarketTabRoute(FOOTBALL_MARKET_MANAGER_SALES, true)}
                    matches={[
                      FOOTBALL_MARKET_MANAGER_SALES,
                      FOOTBALL_MARKET_MANAGER_SALES_PLAYERSLUG_RARITY,
                    ]}
                  >
                    <FormattedMessage {...navLabels.purchases} />
                  </Tab>
                  <Tab
                    to={getMarketTabRoute(FOOTBALL_MARKET_COMMON_PACKS)}
                    matches={[FOOTBALL_MARKET_COMMON_PACKS]}
                  >
                    <Badge badgeContent={unclaimedMarketTasksCount}>
                      <FormattedMessage {...navLabels.commonPacks} />
                    </Badge>
                  </Tab>
                </>
              )}
            </TabBar>
          </FullWidthContainer.NoPadding>
        }
      >
        {content}
      </ContentWithStickyHeader>
    </FullWidthContainer>
  );
};

export default MarketLayout;
